import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recruitmentCompanyService } from '../../services/recruitmentCompanyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const ViewEmployeeProfile = () => {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadProfile();
    }, [employeeId]);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await recruitmentCompanyService.getEmployeeProfile(employeeId);
            const profileData = response?.data || response;
            setProfile(profileData);
        } catch (error) {
            console.error('Error loading employee profile:', error);
            const message = error?.response?.data?.message || 'Failed to load employee profile';
            toast.error(message);
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const months = locale === 'ar'
            ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
            : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    const downloadCV = (cvFile) => {
        if (!cvFile) {
            toast.error(t('employee.profile.noCvText') || 'No CV available');
            return;
        }
        // CV file is already a full URL from the API (asset('storage/' . $this->cv_file))
        const url = cvFile.startsWith('http') ? cvFile : `${STORAGE_BASE_URL}/${cvFile}`;
        window.open(url, '_blank');
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!profile) {
        return <div className="text-center py-4" dir={locale === 'ar' ? 'rtl' : 'ltr'}>{t('employee.vacancyDetail.vacancyNotFound') || 'Profile not found'}</div>;
    }

    const employee = profile;
    const userImageRaw = employee.user?.image;
    const userImage = userImageRaw ? (userImageRaw.startsWith('http') ? userImageRaw : `${STORAGE_BASE_URL}/${userImageRaw}`) : null;
    const userName = employee.user?.name || 'اسم المستخدم';
    const jobTitle = employee.job_title?.name || 'المسمي الوظيفي';
    const bio = employee.bio || '';
    const email = employee.user?.email || '';
    const phone = employee.user?.phone || '';
    const cvFile = employee.cv_file;
    const skills = employee.skills || [];
    const jobHistories = employee.job_histories || [];
    const educationHistories = employee.education_histories || [];

    return (
        <div className="employee-profile-page" dir={locale === 'ar' ? 'rtl' : 'ltr'} style={{ background: '#f8fafc', minHeight: '100vh' }}>
            <RecruitmentCompanyNav user={user} />

            {/* Modern Header */}
            <div className="px-3 mb-4 mt-3">
                <div className="d-flex align-items-center gap-3 mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h3 style={{
                            margin: 0,
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#111827',
                            letterSpacing: '-0.02em'
                        }}>
                            {t('company.vacancyApplications.applicant') || 'Employee Profile'}
                        </h3>
                    </div>
                </div>
            </div>

            {/* Modern Profile Header Card */}
            <div style={{
                background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(163 189 228) 100%)',
                borderRadius: '20px',
                padding: '2.5rem 1.5rem',
                margin: '0 12px 2rem 12px',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.25)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative background pattern */}
                <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-10%',
                    width: '300px',
                    height: '300px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    filter: 'blur(40px)'
                }} />
                <div style={{
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-10%',
                    width: '250px',
                    height: '250px',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '50%',
                    filter: 'blur(30px)'
                }} />

                <div className="d-flex flex-column align-items-center" style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        marginBottom: '1.25rem',
                        background: 'white'
                    }}>
                        {userImage ? (
                            <img
                                src={userImage}
                                alt={userName}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'white',
                                color: '#667eea',
                                fontSize: '3rem',
                                fontWeight: '700'
                            }}>
                                {userName.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                    <h2 style={{
                        margin: 0,
                        marginBottom: '0.5rem',
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: 'white',
                        textShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        {userName}
                    </h2>
                    {jobTitle && (
                        <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            padding: '0.5rem 1.25rem',
                            borderRadius: '20px',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.3)'
                        }}>
                            {jobTitle}
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Information Card */}
            {(email || phone) && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 1rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1.25rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        {t('employee.profile.contactInformation') || 'Contact Information'}
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {email && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                                <span style={{ color: '#475569', fontSize: '0.95rem', fontWeight: '500' }}>{email}</span>
                            </div>
                        )}
                        {phone && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                                <span style={{ color: '#475569', fontSize: '0.95rem', fontWeight: '500' }}>{phone}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bio Section Card */}
            {bio && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 1rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        {t('employee.profile.aboutMe') || 'About'}
                    </h3>
                    <p style={{
                        margin: 0,
                        color: '#475569',
                        fontSize: '0.95rem',
                        lineHeight: '1.7',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {bio}
                    </p>
                </div>
            )}

            {/* Skills Section Card */}
            {skills.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 1rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1.25rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                        </svg>
                        {t('employee.profile.skills') || 'Skills'}
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                        {skills.map((skill) => (
                            <span
                                key={skill.id}
                                style={{
                                    padding: '0.625rem 1.125rem',
                                    borderRadius: '12px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(163 189 228) 100%)',
                                    color: 'white',
                                    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.25)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.35)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(102, 126, 234, 0.25)';
                                }}
                            >
                                {skill.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Job History Section Card */}
            {jobHistories.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 1rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                        {t('employee.profile.workExperience') || 'Work Experience'}
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {jobHistories.map((job, index) => (
                            <div key={job.id}>
                                <div style={{
                                    padding: '1.25rem',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f1f5f9';
                                    e.currentTarget.style.borderColor = '#cbd5e1';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#f8fafc';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                                >
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <h4 style={{
                                            margin: 0,
                                            marginBottom: '0.375rem',
                                            fontSize: '1.125rem',
                                            fontWeight: '700',
                                            color: '#111827'
                                        }}>
                                            {job.company_name}
                                        </h4>
                                        {job.job_title && (
                                            <p style={{
                                                margin: 0,
                                                fontSize: '0.95rem',
                                                fontWeight: '600',
                                                color: '#4a7bc4'
                                            }}>
                                                {job.job_title.name}
                                            </p>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontSize: '0.875rem',
                                            color: '#64748b'
                                        }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                            <span>
                                                {t('employee.profile.from') || 'From'} {formatDate(job.from_date)} {t('employee.profile.until') || 'Until'} {job.to_date ? formatDate(job.to_date) : (t('employee.profile.untilNow') || 'Now')}
                                            </span>
                                        </div>
                                        {job.description && (
                                            <p style={{
                                                margin: 0,
                                                marginTop: '0.5rem',
                                                fontSize: '0.9rem',
                                                color: '#475569',
                                                lineHeight: '1.6',
                                                whiteSpace: 'pre-wrap'
                                            }}>
                                                {job.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {index < jobHistories.length - 1 && (
                                    <div style={{ height: '1rem' }} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Education History Section Card */}
            {educationHistories.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 1rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                            <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                        </svg>
                        {t('employee.profile.educationHistory') || t('employee.profile.education') || 'Education'}
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {educationHistories.map((edu, index) => (
                            <div key={edu.id}>
                                <div style={{
                                    padding: '1.25rem',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f1f5f9';
                                    e.currentTarget.style.borderColor = '#cbd5e1';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#f8fafc';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                                >
                                    <h4 style={{
                                        margin: 0,
                                        marginBottom: '0.75rem',
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: '#111827'
                                    }}>
                                        {edu.academic_degree} {edu.specialization ? `- ${edu.specialization}` : ''}
                                    </h4>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontSize: '0.875rem',
                                        color: '#64748b'
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <span>
                                            {t('employee.profile.from') || 'From'} {formatDate(edu.from_date)} {t('employee.profile.until') || 'Until'} {edu.to_date ? formatDate(edu.to_date) : (t('employee.profile.untilNow') || 'Now')}
                                        </span>
                                    </div>
                                </div>
                                {index < educationHistories.length - 1 && (
                                    <div style={{ height: '1rem' }} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* CV Section Card */}
            {cvFile && (
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    margin: '0 12px 2rem 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    border: '1px solid #f1f5f9'
                }}>
                    <h3 style={{
                        margin: 0,
                        marginBottom: '1.25rem',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4a7bc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                        {t('employee.profile.cv') || 'CV/Resume'}
                    </h3>
                    <div
                        onClick={() => downloadCV(cvFile)}
                        style={{
                            cursor: 'pointer',
                            padding: '1.25rem',
                            background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(163 189 228) 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                </svg>
                            </div>
                            <div>
                                <div style={{
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    color: 'white',
                                    marginBottom: '0.25rem'
                                }}>
                                    {t('employee.profile.viewCv') || t('employee.profile.cv') || 'View CV'}
                                </div>
                                <div style={{
                                    fontSize: '0.875rem',
                                    color: 'rgba(255,255,255,0.9)',
                                    fontWeight: '500'
                                }}>
                                    {t('employee.profile.downloadCv') || 'Click to view or download'}
                                </div>
                            </div>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </div>
                </div>
            )}

            <RecruitmentCompanyBottomNavigation />
        </div>
    );
};

export default ViewEmployeeProfile;

